import React, { useState, useEffect } from 'react';
import { updateDoctor } from '../../logic/settings';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, FormControl, Row, Col, Container } from 'react-bootstrap';
import { clearStorage } from './../../libs/general';
import access from 'safe-access';

function DoctorBilling() {
	const navigate = useNavigate();
	const location = useLocation();
	const params = useParams();
	const [doc, setDoc] = useState(null);
	const [error, setError] = useState('');
	
	const newServiceProperties = () => {
		return {
			title: '',
			price: '',
			currency: 'eur',
			bought: false
		};
	};
	
	useEffect(() => {
		if (location && location.state && location.state.doc) {
			const { doc } = location.state;
			// Ensure docId is present - use URL param as fallback if missing
			const docWithId = { ...doc };
			if (!docWithId.docId && params.docId) {
				docWithId.docId = params.docId;
			}
			setDoc(docWithId);
			
			if (!(access(docWithId, "preferences.billing.services")
				&& docWithId.preferences.billing.services instanceof Array
				&& docWithId.preferences.billing.services.length > 0)) {
				// Use functional update to ensure we have the latest doc state
				setDoc(prevDoc => {
					const currentDoc = prevDoc || docWithId;
					if (!currentDoc.preferences) currentDoc.preferences = {};
					if (!currentDoc.preferences.billing) {
						currentDoc.preferences.billing = {
							doctorRate: '',
							clinicFee: '',
							toBePaidAt: '',
							services: [newServiceProperties()]
						};
					}
					if (!currentDoc.preferences.billing.services) {
						currentDoc.preferences.billing.services = [];
					}
					if (currentDoc.preferences.billing.services.length <= 0) {
						currentDoc.preferences.billing.services.push(newServiceProperties());
					}
					return { ...currentDoc };
				});
			}
		}
	}, [location, params.docId]);
	
	const handleChange = (e, i) => {
		if (e && e.target && e.target.id && e.target.value !== null) {
			const field = e.target.id;
			const value = e.target.value;

			const updatedDoc = {...doc};
			updatedDoc.preferences.billing.services[i][field] = value;
			setDoc(updatedDoc);
		} else return;
	};
	
	const handleSave = async (e) => {
		e.preventDefault();
		e.stopPropagation();

		// Ensure docId is present - use URL param as fallback if missing from doc object
		const docToSave = { ...doc };
		if (!docToSave.docId && params.docId) {
			docToSave.docId = params.docId;
		}

		const updatedDoc = await updateDoctor(docToSave);
		clearStorage();

		if (updatedDoc instanceof Error)
			handleError(updatedDoc);
		else
			navigate("/settings/doctors");
	};
	
	const handleError = (e) => {
		console.log(e);
		setError(e);
	};
	
	const addBilling = () => {
		const updatedDoc = {...doc};

		if (!updatedDoc.preferences) updatedDoc.preferences = {};
		if (!updatedDoc.preferences.billing) updatedDoc.preferences.billing = {
			doctorRate: '',
			clinicFee: '',
			toBePaidAt: '',
			services: [newServiceProperties()]
		};

		if (!updatedDoc.preferences.billing.services) updatedDoc.preferences.billing.services = [];
		if (updatedDoc.preferences.billing.services.length <= 0) updatedDoc.preferences.billing.services.push(newServiceProperties());

		setDoc(updatedDoc);
	};
	
	const addService = (e) => {
		e.preventDefault();

		const updatedDoc = {...doc};
		updatedDoc.preferences.billing.services.push(newServiceProperties());
		setDoc(updatedDoc);
	};
	
	const deleteService = (e, index) => {
		if (window.confirm('Are you sure you want to delete this service?')) {
			e.preventDefault();

			const updatedDoc = {...doc};

			if (!(updatedDoc.preferences || updatedDoc.preferences.billing
				|| updatedDoc.preferences.billing
				|| updatedDoc.preferences.billing.services instanceof Array
				|| updatedDoc.preferences.billing.services[index]))
				return;

			updatedDoc.preferences.billing.services.splice(index, 1);
			setDoc(updatedDoc);
		}
	};
	
	// Only render if doc is loaded
	if (!doc) return <div>Loading...</div>;
	
	return (
		<div>
			<div className="pages__back-button--container">
				<FontAwesomeIcon className="pages__back-button--icon" icon="chevron-left"></FontAwesomeIcon>
				<Link className="pages__back-button--text" to="/settings/doctors">Back</Link>
			</div>
			<header className="pages__header--container centered-text">
				<h1>Doctor's Billing</h1>
			</header>
			<div className="medium-push"></div>
			<Container>
				<form autoComplete='on' onSubmit={handleSave} style={{ paddingBottom: '60px' }}>
					<Row>
						<Col xs={{ span: 12, offset: 1 }} sm={{ span: 12, offset: 1 }} md={{ span: 4, offset: 1 }} lg={{ span: 4, offset: 1 }}
							className="billing-settings__services--col">
							<h3 className="centered-text">Services</h3>
							{
								(access(doc, "preferences.billing.services")
									&& doc.preferences.billing.services instanceof Array
									&& doc.preferences.billing.services.length >= 1) ?
									doc.preferences.billing.services.map((service, i) => {
										return (
											<fieldset className="billing-settings__form--item" key={i}>
												<Col xs={9} sm={9} md={9} lg={9}>
													<Row>
														<Form.Group className="mb-3">
															<FormControl
																id='title'
																type='text'
																placeholder="Title"
																required
																value={service.title}
																onChange={(e) => handleChange(e, i)}>
															</FormControl>
														</Form.Group>
													</Row>
													<Row>
														<Col xs={6} sm={6} md={6} lg={6}>
															<Form.Group className="mb-3">
																<FormControl
																	as="select"
																	id='currency'
																	disabled
																	value='eur'
																	onChange={(e) => handleChange(e, i)}>
																	<option value='eur'>EUR</option>
																</FormControl>
															</Form.Group>
														</Col>
														<Col xs={6} sm={6} md={6} lg={6}>
															<Form.Group className="mb-3">
																<FormControl
																	id='price'
																	placeholder="Price"
																	type='number'
																	required
																	value={service.price}
																	onChange={(e) => handleChange(e, i)}>
																</FormControl>
															</Form.Group>
														</Col>
													</Row>
												</Col>
												<Col xs={{ span: 1, offset: 1 }} sm={{ span: 1, offset: 1 }} md={{ span: 1, offset: 1 }} lg={{ span: 1, offset: 1 }}
													className="pages__form--col settings__list--actionIcons right-text no-border">
													<FontAwesomeIcon className="settings__list--delete" onClick={(e) => deleteService(e, i)} icon="trash-alt"></FontAwesomeIcon>
												</Col>
											</fieldset>
										)
									})
									: undefined
							}
							<div className="pages__action-bar--container">
								<div onClick={addService} className="pages__action-bar--item">
									<FontAwesomeIcon icon="plus" />
								</div>
							</div>
						</Col>
						<Col xs={{ span: 4, offset: 1 }} sm={{ span: 4, offset: 1 }} md={{ span: 4, offset: 1 }} lg={{ span: 4, offset: 1 }}>
							<Form.Group className="mb-3">
								<Form.Label>Doctor Rate</Form.Label>
								<FormControl
									id='doctorRate'
									type='number'
									required
									defaultValue={access(doc, "preferences.billing.doctorRate") ? doc.preferences.billing.doctorRate : null}
									onChange={(e) => setDoc({ ...doc, preferences: { ...doc.preferences, billing: { ...doc.preferences.billing, doctorRate: e.target.value } } })}>
								</FormControl>
							</Form.Group>
							<Form.Group className="mb-3">
								<Form.Label>Clinic Fee</Form.Label>
								<FormControl
									id='clinicFee'
									type='number'
									required
									defaultValue={access(doc, "preferences.billing.clinicFee") ? doc.preferences.billing.clinicFee : null}
									onChange={(e) => setDoc({ ...doc, preferences: { ...doc.preferences, billing: { ...doc.preferences.billing, clinicFee: e.target.value } } })}>
								</FormControl>
							</Form.Group>
							<Form.Group className="mb-3">
								<Form.Label>To be paid at</Form.Label>
								<FormControl
									as="select"
									required
									id='toBePaidAt'
									defaultValue={access(doc, "preferences.billing.toBePaidAt") ? doc.preferences.billing.toBePaidAt : null}
									onChange={(e) => setDoc({ ...doc, preferences: { ...doc.preferences, billing: { ...doc.preferences.billing, toBePaidAt: e.target.value } } })}>
									<option value="">--</option>
									<option value="pharmacycounter">Pharmacy Counter</option>
									<option value="doctorinclinic">Doctor In Clinic</option>
								</FormControl>
							</Form.Group>
							<Form.Group className="mb-3">
								<Form.Label>Currency</Form.Label>
								<FormControl
									as="select"
									id='currency'
									value='eur'
									disabled>
									<option value='eur'>EUR</option>
								</FormControl>
							</Form.Group>
						</Col>
					</Row>
					<Row>
						<div className="medium-push"></div>
						<div className="pages__form--error centered-text">
							{
								error && error instanceof Array ?
									error.map((err, i) => <p key={i}>{err.toString()}</p>)
									: <p>{error.message}</p>
							}
						</div>
						<div>
							<input className="save-button center-block small-button" type='submit' value='Save' />
						</div>
					</Row>
				</form>
			</Container>
		</div>
	);
}

export default DoctorBilling;